//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import type { SignedApplicationAssetResponse } from '@/types/Shared';

//Component Begin

export default function OrganizationDocuments() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
  } = useFormContext();

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        const updatedStep2 = { ...getValues('step2') };

        res.forEach((r, resIndex) => {
          const { sign } = r.value;
          const currentPayload = payloads[resIndex];
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          const relevantFiles = currentPayload.files.filter((file) =>
            currentPayload.signAssetPayload.some((payload) => payload.fileName === file.name)
          );

          updatedStep2[currentPayload.field.key] = {
            ...updatedStep2[currentPayload.field.key],
            selectedFiles: signRes.map((sig, index) => ({
              name: relevantFiles[index].name,
              path: sig.path,
              key: sig.key,
              type: relevantFiles[index].type,
              field: currentPayload.field.key,
              description: currentPayload.field.description,
            })),
          };
        });

        updateData({
          step2: updatedStep2,
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step2 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step2)[0];
      const firstErrorChildKey = Object.keys(get(errors, `step2.${firstErrorKey}`))[0];
      setFocus(`step2.${firstErrorKey}.${firstErrorChildKey}`);
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step2');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step2[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);

        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();

    if (!isValid) {
      return;
    }
    const formData = getValues('step2');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step2: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Organization Documents</h3>
        <p className='mt-1 text-sm text-gray-500'>Please provide the following documents.</p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <HookFileInput name='step2.f1.selectedFiles' label='Articles of Incorporation' id='selectedFiles' multiple />
        </div>
        <div className='col-span-6'>
          <HookFileInput name='step2.f2.selectedFiles' label='By Laws' id='selectedFiles' multiple />
        </div>
        <div className='col-span-6'>
          <HookFileInput name='step2.f3.selectedFiles' label='Charters' id='selectedFiles' multiple />
        </div>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>Certificate of Good Standing</h3>
          <p className='text-xs text-gray-500'>Must be Active and Current </p>
          <HookFileInput name='step2.f4.selectedFiles' id='selectedFiles' multiple />
        </div>
        <div className='col-span-6'>
          <HookFileInput name='step2.f5.selectedFiles' label='Map of Service Area' id='selectedFiles' multiple />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
